import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useMdr } from '../../hooks/useMdr';

interface MerchantItem {
  merchantId: string;
  code: string;
  name: string;
  status: 'active' | 'inactive' | 'locked';
  lastStatSyncAt: string | null;
  masterPayinFeePercent: number;
  masterPayoutFeePercent: number;
  payinFeePercent?: number;
  payoutFeePercent?: number;
}

export function MdrMerchants() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSubMdr } = useMdr();

  const { data, isLoading } = useQuery({
    queryKey: ['mdr', 'merchants'],
    queryFn: () => apiGet<{ items: MerchantItem[]; total: number }>('/mdr/merchants'),
  });

  const items = data?.ok ? data.data.items : [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
        {t('mdr.merchantsTitle')}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('mdr.merchantsTitle')} ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && items.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('merchants.code')}</TableHead>
                    <TableHead>{t('merchants.name')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    {!isSubMdr && (
                      <>
                        <TableHead className="text-right">{t('mdr.masterPayinFee')}</TableHead>
                        <TableHead className="text-right">{t('mdr.masterPayoutFee')}</TableHead>
                      </>
                    )}
                    {isSubMdr && (
                      <>
                        <TableHead className="text-right">{t('mdr.myPayinFee')}</TableHead>
                        <TableHead className="text-right">{t('mdr.myPayoutFee')}</TableHead>
                      </>
                    )}
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((m) => (
                    <TableRow key={m.merchantId}>
                      <TableCell className="font-mono text-xs">{m.code}</TableCell>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>
                        <Badge variant={m.status === 'active' ? 'success' : 'danger'}>
                          {t(`common.${m.status}`)}
                        </Badge>
                      </TableCell>
                      {!isSubMdr && (
                        <>
                          <TableCell className="text-right">{m.masterPayinFeePercent}%</TableCell>
                          <TableCell className="text-right">{m.masterPayoutFeePercent}%</TableCell>
                        </>
                      )}
                      {isSubMdr && (
                        <>
                          <TableCell className="text-right text-blue-600">
                            {m.payinFeePercent ?? 0}%
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {m.payoutFeePercent ?? 0}%
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/mdr/merchants/${m.merchantId}`)}
                        >
                          {t('merchants.viewStats')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
